import React from 'react'
import styled from 'styled-components'
import ProgressBar from '@ramonak/react-progress-bar'
import { isEqual } from 'lodash'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import reactStringReplace from 'react-string-replace'

import { cn } from 'utils/classMerge'

import { CloseButton, LinkButton, OutlineButton } from '../buttons/Buttons'
import { fonts, palette, mediaQueries } from '../../styles/variables'
import { translate, getLanguageCode } from '../../utils/translation'
import { getKeywordsCount } from '../../utils/functions'
import {
  getMaxValuesForUser,
  getMaxValuesForProject,
  shouldBlockFeature,
  DASHBOARD,
  isProUser,
} from '../../utils/freeVersionBlock'
import SwitchToggle from '../buttons/SwitchToggle'
import {
  KISSMETRICS_TRACK_EVENTS,
  kissmetricsRecordingEvent,
  PROPERTY_NAMES,
  PROPERTY_VALUES,
} from '../../utils/kissmetrics'
import { UberInputField } from '../input/InputField'
import { LoadingResponsive } from '../loading/Loading'
import { Text } from '../topography'
import Tooltip from '../tooltip/Tooltip'
import { TableNotification, TableNotificationBar } from '../tables/styled'
import { isSubUser } from '../../utils/userAuth'
import { selectPromptModal } from '../../state/selectors'
import { updatePromptModal } from '../../state/actions/global'
import { MIXPANEL_PROPS } from '../../utils/mixpanel'
import { checkoutModalHandler } from '../../utils/sendMessage'

import ConfirmationModal from './ConfirmationModal'
import Modal, { ModalHeader, ModalContent, ModalFooter } from './Modal'

const FormData = styled.div((props) => ({
  fontSize: '14px',
  marginBottom: '24px',
  ...props.styles,
}))

const Label = styled.div((props) => ({
  fontFamily: fonts.primaryMedium,
  width: props.longLabel ? '205px' : '195px',
  [mediaQueries.mobile]: {},
}))

const desktopStyle = {
  width: '565px',
  height: 'fit-content',
  maxHeight: '95vh',
  overflow: 'unset',
  overflowY: 'auto',
}
const mobileStyle = {
  width: '100%',
}

class ProjectSettingsModal extends React.PureComponent {
  constructor(props) {
    super(props)

    this.state = {
      project: props.project,
      title: props.project?.title || '',
      domain: props.project?.domain || '',
      frequency: getMaxValuesForUser().RANKING_UPDATE_FREQUENCY,
      mobileTracking: !shouldBlockFeature(
        DASHBOARD,
        'actions',
        `mobile_rank_tracking_on`,
      ),
      alertsEnabled: props.project?.alerts_enabled || false,
      invalidDomain: null,
      deleteModal: {
        isOpen: false,
      },
      deleteProjectErrorMessage: null,
      isUpgradePromptModalOpen: props.promptModal.isOpen,
    }
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (
      nextProps.project &&
      (!isEqual(prevState.project, nextProps.project) || !nextProps.isOpen)
    ) {
      return {
        title: nextProps.project.title,
        domain: nextProps.project.domain,
        frequency: nextProps.project.update_freq.toLowerCase(),
        mobileTracking: nextProps.project.mobile_rank_tracking,
        alertsEnabled: nextProps.project.alerts_enabled,
        invalidDomain: null,
        deleteProjectErrorMessage: null,
        isUpgradePromptModalOpen: nextProps.promptModal.isOpen,
        project: nextProps.project,
      }
    }
    if (
      nextProps.isOpen &&
      prevState.isUpgradePromptModalOpen &&
      !nextProps.promptModal.isOpen
    ) {
      return {
        frequency: getMaxValuesForUser().RANKING_UPDATE_FREQUENCY,
        mobileTracking: !shouldBlockFeature(
          DASHBOARD,
          'actions',
          `mobile_rank_tracking_on`,
        ),
        isUpgradePromptModalOpen: nextProps.promptModal.isOpen,
      }
    }
    return {
      isUpgradePromptModalOpen: nextProps.promptModal.isOpen,
    }
  }

  handleChangeName = (e) => {
    this.setState({ title: e.target.value })
  }

  handleChangeDomain = (e) => {
    this.setState({ domain: e.target.value, invalidDomain: null })
  }

  handleChangeNotification = (alertsEnabled) => {
    this.setState({ alertsEnabled })
  }

  handleChangeFrequency = (e) => {
    const frequency = e === true ? 'daily' : 'weekly'
    if (
      shouldBlockFeature(
        DASHBOARD,
        'actions',
        `rank_update_frequency_${frequency}`,
      )
    ) {
      this.props.updatePromptModal({
        isOpen: true,
        title: translate('upgrade_prompt_frequency_heading'),
        message: translate('upgrade_prompt_frequency_text'),
        eventProperty: {
          [PROPERTY_NAMES.btn_location]: PROPERTY_VALUES.dashboard.updates,
          btnLocation:
            MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.values.UPGRADE_MODAL,
        },
        forceUpgrade: true,
      })
      return null
    }
    this.setState({ frequency })
  }

  handleChangeMobileTracking = (mobileTracking) => {
    if (shouldBlockFeature(DASHBOARD, 'actions', `mobile_rank_tracking_on`)) {
      this.props.updatePromptModal({
        isOpen: true,
        title: translate('upgrade_prompt_mobile_heading'),
        message: translate('upgrade_prompt_mobile_text'),
        eventProperty: {
          [PROPERTY_NAMES.btn_location]: PROPERTY_VALUES.dashboard.mobile,
          btnLocation:
            MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.values.UPGRADE_MODAL,
        },
        forceUpgrade: true,
      })
      return null
    }
    this.setState({ mobileTracking })
  }

  handleClickRemoveProject = () => {
    if (isSubUser()) {
      this.setState({
        deleteProjectErrorMessage: translate(
          'remove_project_invited_user_warning',
        ),
      })
    } else {
      this.setState({ deleteModal: { isOpen: true } })
    }
  }

  getProjectProgressColor = (projectProgress) => {
    if (projectProgress > 74) {
      return palette.red
    }
    if (projectProgress > 49) {
      return palette.yellow
    }
    return palette.green
  }

  render() {
    const { onSave, project, isLoading, onRequestClose, isOpen, ...rest } =
      this.props
    if (!isOpen) return null
    const { MAX_KEYWORDS_PER_PROJECT } = getMaxValuesForProject(project)
    const {
      frequency,
      mobileTracking,
      alertsEnabled,
      deleteProjectErrorMessage,
      deleteModal,
      domain,
      invalidDomain,
      title,
    } = this.state
    const languageCode = getLanguageCode()
    const longLabel = languageCode === 'es'

    const projectProgress =
      (getKeywordsCount(project.keywords || {}) / MAX_KEYWORDS_PER_PROJECT) *
      100

    const projectProgressColor = this.getProjectProgressColor(projectProgress)

    const isSubUserAccount = isSubUser()

    const switchToggleClass = cn({
      'tw-mr-8': languageCode === 'pt' || languageCode === 'ja',
      'tw-mr-5': languageCode === 'en',
      'tw-mr-1': languageCode === 'de',
      'tw-mr-0': languageCode === 'it',
      'tw-mr-10': languageCode === 'es',
      'tw-mr-[6px]': languageCode === 'fr',
      'tw-mr-[17px]': languageCode === 'zh',
    })

    return (
      <>
        <Modal
          isOpen={isOpen}
          desktopStyle={desktopStyle}
          mobileStyle={mobileStyle}
          parentSelector={() => document.getElementById('prompt-modal-page')}
          {...rest}
        >
          <ModalHeader
            style={{ paddingTop: '20px' }}
            data-testid="project_settings_modal_title"
          >
            {translate('project_settings')}
            <CloseButton
              onClick={() => onRequestClose(false)}
              styles={{ top: '15px' }}
            />
          </ModalHeader>
          <ModalContent
            padding="20px 40px"
            styles={{
              height: 'calc(100% - 258px)',
              [mediaQueries.mobile]: {
                height: 'auto',
              },
            }}
          >
            {deleteProjectErrorMessage && (
              <TableNotificationBar
                data-testid="project_settings_modal_notification"
                key="notificaton-bar"
                style={{ margin: '0 0 24px 0' }}
              >
                <TableNotification
                  type="error"
                  content={
                    <Text
                      boldColor={palette.darkGray}
                      dangerouslySetInnerHTML={{
                        __html: translate(
                          'remove_project_invited_user_warning',
                        ),
                      }}
                    />
                  }
                  hideClose
                />
              </TableNotificationBar>
            )}
            <FormData data-testid="project_settings_modal_project_title">
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Label longLabel={longLabel} style={{ marginBottom: '12px' }}>
                  {translate('project_title')}
                </Label>
                <LinkButton
                  data-testid="remove-project-button"
                  style={{
                    textDecoration: 'underline',
                    fontFamily: fonts.primaryBook,
                    whiteSpace: 'pre',
                  }}
                  onClick={this.handleClickRemoveProject}
                >
                  {translate('remove_this_project')}
                </LinkButton>
              </div>
              <UberInputField
                data-testid="input_project_title"
                placeholder={translate('projecttitle')}
                name="domain"
                value={title}
                onChange={this.handleChangeName}
                height="48px"
                disabled={isSubUserAccount}
              />
            </FormData>
            <FormData data-testid="project_settings_modal_domain">
              <Label longLabel={longLabel} style={{ marginBottom: '12px' }}>
                {translate('domain')}
              </Label>
              <UberInputField
                data-testid="input_domain"
                placeholder={translate('filter_enter_domain')}
                name="domain"
                value={domain}
                onChange={this.handleChangeDomain}
                invalidMessage={invalidDomain}
                height="48px"
                disabled={project.pstatus !== 'inactive'}
              />
            </FormData>
            <FormData data-testid="project_settings_modal_keywords">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: '12px',
                }}
              >
                <Label longLabel={longLabel} style={{ width: 'fit-content' }}>
                  {translate('keywords')}
                </Label>
                <Text
                  fontSize="14px"
                  boldColor={palette.darkGray}
                  margin="0"
                  dangerouslySetInnerHTML={{
                    __html: translate(
                      'available_keywords',
                      getKeywordsCount(project.keywords || {}),
                      MAX_KEYWORDS_PER_PROJECT,
                    ),
                  }}
                />
              </div>
              <ProgressBar
                completed={Math.max(1, projectProgress)}
                height="12px"
                bgColor={projectProgressColor}
                baseBgColor={palette.lightGray30}
                isLabelVisible={false}
                className="progress-bar"
              />
            </FormData>
            <FormData
              data-testid="project_settings_modal_settings_frequency"
              styles={{
                display: 'flex',
                justifyContent: 'space-between',
                border: `1px solid ${palette.lightGray}`,
                borderRadius: '2px',
                padding: isProUser() ? '3px 16px' : '16px',
                marginBottom: '16px',
                [mediaQueries.mobile]: {
                  flexDirection: 'column',
                  '& .switcher': {
                    marginTop: '16px',
                  },
                },
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  width: '100%',
                }}
              >
                <Label longLabel={longLabel}>
                  {translate('step4_rank_update_frequency')}
                  <Tooltip
                    id="rank_update_frequency_tooltip"
                    text={translate('step4_rank_update_frequency_description')}
                    style={{ marginLeft: '8px' }}
                  />
                </Label>
                {!isProUser() && (
                  <Text margin="16px 0 0 0">
                    {reactStringReplace(
                      translate(
                        'upgrade_for_daily_rank_tracking',
                        translate('upgrade'),
                      ),
                      translate('upgrade'),
                      (match, index) => {
                        return (
                          <LinkButton
                            key={index}
                            onClick={() => {
                              checkoutModalHandler({
                                mixpanelProps: [
                                  MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.values
                                    .PROJECT_SETTINGS_MODAL,
                                  MIXPANEL_PROPS.BUTTON_CTA.values.UPGRADE,
                                ],
                              })
                              kissmetricsRecordingEvent(
                                KISSMETRICS_TRACK_EVENTS.clicked_upgrade,
                                {
                                  [PROPERTY_NAMES.btn_location]:
                                    PROPERTY_VALUES.dashboard.updates,
                                },
                              )
                            }}
                            style={{
                              textDecoration: 'underline',
                              fontSize: '14px',
                              margin: '0px',
                              display: 'inline-block',
                              fontFamily: fonts.primaryBook,
                            }}
                          >
                            {translate('upgrade')}
                          </LinkButton>
                        )
                      },
                    )}
                  </Text>
                )}
              </div>
              <SwitchToggle
                disableDrag
                leftElement={translate('weekly')}
                rightElement={translate('daily')}
                checked={frequency === 'daily'}
                onChange={this.handleChangeFrequency}
                offColor={palette.lightGray30}
                offHandleColor={palette.gray}
                disabled={isSubUserAccount}
                className={switchToggleClass}
              />
            </FormData>
            <FormData
              data-testid="project_settings_modal_settings_mobile"
              styles={{
                display: 'flex',
                justifyContent: 'space-between',
                border: `1px solid ${palette.lightGray}`,
                borderRadius: '2px',
                padding: isProUser() ? '3px 16px' : '16px',
                marginBottom: '16px',
                [mediaQueries.mobile]: {
                  flexDirection: 'column',
                  '& .switcher': {
                    marginTop: '16px',
                  },
                },
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  width: '100%',
                }}
              >
                <Label longLabel={longLabel}>
                  {translate('step4_mobile_rank_tracking')}
                  <Tooltip
                    id="upgrade_for_mobile_rank_tooltip"
                    text={translate('step4_mobile_rank_tracking_description')}
                    style={{ marginLeft: '8px' }}
                  />
                </Label>
                {!isProUser() && (
                  <Text margin="16px 0 0 0">
                    {reactStringReplace(
                      translate(
                        'upgrade_for_mobile_rank',
                        translate('upgrade'),
                      ),
                      translate('upgrade'),
                      (match, index) => {
                        return (
                          <LinkButton
                            key={index}
                            onClick={() => {
                              checkoutModalHandler({
                                mixpanelProps: [
                                  MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.values
                                    .PROJECT_SETTINGS_MODAL,
                                  MIXPANEL_PROPS.BUTTON_CTA.values.UPGRADE,
                                ],
                              })
                              kissmetricsRecordingEvent(
                                KISSMETRICS_TRACK_EVENTS.clicked_upgrade,
                                {
                                  [PROPERTY_NAMES.btn_location]:
                                    PROPERTY_VALUES.dashboard.mobile,
                                },
                              )
                            }}
                            style={{
                              textDecoration: 'underline',
                              fontSize: '14px',
                              margin: '0px',
                              display: 'inline-block',
                              fontFamily: fonts.primaryBook,
                            }}
                          >
                            {translate('upgrade')}
                          </LinkButton>
                        )
                      },
                    )}
                  </Text>
                )}
              </div>
              <SwitchToggle
                disableDrag
                leftElement={translate('off')}
                rightElement={translate('on')}
                checked={mobileTracking}
                onChange={this.handleChangeMobileTracking}
                offColor={palette.lightGray30}
                offHandleColor={palette.gray}
                disabled={isSubUserAccount}
                className={cn('tw-mr-8', {
                  'tw-mr-[60px]': ['it', 'fr'].includes(languageCode),
                })}
              />
            </FormData>
            <FormData
              data-testid="project_settings_modal_settings_notifications"
              styles={{
                display: 'flex',
                justifyContent: 'space-between',
                border: `1px solid ${palette.lightGray}`,
                borderRadius: '2px',
                padding: isProUser() ? '3px 16px' : '16px',
                marginBottom: '0px',
                [mediaQueries.mobile]: {
                  flexDirection: 'column',
                  '& .switcher': {
                    marginTop: '16px',
                  },
                },
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  width: '100%',
                }}
              >
                <Label longLabel={longLabel}>
                  {translate('notifications')}
                </Label>
              </div>
              <SwitchToggle
                disableDrag
                leftElement={translate('off')}
                rightElement={translate('on')}
                checked={alertsEnabled}
                onChange={this.handleChangeNotification}
                offColor={palette.lightGray30}
                offHandleColor={palette.gray}
                disabled={isSubUserAccount}
                className={cn('tw-mr-8', {
                  'tw-mr-[60px]': ['it', 'fr'].includes(languageCode),
                })}
              />
            </FormData>
          </ModalContent>
          <ModalFooter
            padding="20px 40px"
            data-testid="project_settings_modal_button"
          >
            <OutlineButton
              static
              color={palette.darkGray50}
              onClick={() => onRequestClose(false)}
            >
              {translate('cancel')}
            </OutlineButton>
            <OutlineButton
              static
              onClick={() =>
                onSave(
                  project.id,
                  {
                    title,
                    domain,
                    alerts_enabled: alertsEnabled,
                    rankTrackingFrequency: frequency.toUpperCase(),
                    mobileRankTracking: mobileTracking,
                  },
                  (error) => {
                    if (error) {
                      this.setState({
                        invalidDomain: translate('invalid_domain'),
                      })
                    }
                  },
                )
              }
              disabled={isLoading || invalidDomain || isSubUserAccount}
              style={isLoading ? { padding: 3, verticalAlign: 'bottom' } : {}}
            >
              {isLoading ? (
                <LoadingResponsive
                  size="30px"
                  height="42px"
                  offsetY="5%"
                  color={palette.orange}
                />
              ) : (
                translate('save')
              )}
            </OutlineButton>
          </ModalFooter>
        </Modal>
        <ConfirmationModal
          isOpen={deleteModal.isOpen}
          isLoading={deleteModal.isLoading}
          title={translate('warning')}
          message={translate('delete_project_message')}
          onAccept={() => {
            this.setState({
              deleteModal: {
                ...deleteModal,
                isLoading: true,
              },
            })
            rest.onRemoveProject(() => {
              this.setState({
                deleteModal: {
                  isOpen: false,
                },
              })
              onRequestClose(true)
            })
          }}
          onDeny={() =>
            this.setState({
              deleteModal: {
                isOpen: false,
              },
            })
          }
        />
      </>
    )
  }
}

const mapStateToProps = createStructuredSelector({
  promptModal: selectPromptModal(),
})

const mapDispatchToProps = (dispatch) => ({
  updatePromptModal: (data) => dispatch(updatePromptModal(data)),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ProjectSettingsModal)
